// The "Commercial model" fieldset's arithmetic, extracted from the component
// so the part that can be wrong in money can be tested without a DOM — the
// same split, and the same reason, as partner-vendors-state.ts.
//
// TWO THINGS LIVE HERE AND NOTHING ELSE DOES.
//
// 1. PERCENT IN, FRACTION OUT. Ops types 50 and the column stores 0.5. Every
//    rate on `partners` is a FRACTION (0241's CHECKs are `>= 0 AND <= 1`), and
//    every one of them is money: a 50 that reached the column unconverted
//    would hand a partner fifty times the fee it agreed to, pass the CHECK on
//    nothing (5000% fails the <= 1, but 0.5 typed as 0.5 by an ops user who
//    read the OTHER field's placeholder does not), and only show up on an
//    invoice. The legacy `commission_pct` field above this fieldset takes a
//    raw fraction, which is exactly why these must not: two fields that look
//    alike and read differently is the confusion the share-rule note exists
//    to name.
//
// 2. WHICH FIELDS BELONG TO WHICH MODEL. Rendering all five at once would ask
//    ops to set a markup share on a partner with no markup, and leave stale
//    values on screen that the API would dutifully store. The model decides
//    what is asked, and `fieldsForModel` is the one answer.
//
// What is NOT here: the share RULE (fee share vs legacy commission). That is
// decided by whether `fee_share_pct` is null, which is a property of the draft,
// not of this module's arithmetic — see `shareRuleFor`, which is here only
// because the note it drives has to say the same thing the API's resolver
// does (apps/api/src/modules/pricing/commercial-model.ts).

export type CommercialModel =
  | 'booking_fee'
  | 'supplier_commission'
  | 'markup_supplier_commission';

export const COMMERCIAL_MODELS: readonly CommercialModel[] = [
  'booking_fee',
  'supplier_commission',
  'markup_supplier_commission',
];

/** The five rate fields, by their column names. */
export type CommercialRateField =
  | 'fee_share_pct'
  | 'markup_pct'
  | 'markup_share_pct'
  | 'supplier_commission_share_pct';

/**
 * Which rate fields a model actually uses.
 *
 * Model 1 shares the FEE; Model 2 shares the crew's COMMISSION; Model 3 sets
 * the markup and shares both the markup and the commission. A field outside
 * its model's list is not merely hidden — it is not asked for, because a value
 * sitting in a column the pricing resolver never reads is a number ops will
 * later reconcile against and find absent from every invoice.
 */
export function fieldsForModel(model: CommercialModel): readonly CommercialRateField[] {
  switch (model) {
    case 'booking_fee':
      return ['fee_share_pct'];
    case 'supplier_commission':
      return ['supplier_commission_share_pct'];
    case 'markup_supplier_commission':
      return ['markup_pct', 'markup_share_pct', 'supplier_commission_share_pct'];
  }
}

/**
 * A stored FRACTION as the whole-number percentage ops reads and types.
 *
 * `''` for null, which is what an empty input is: the column is nullable and
 * "no share rule set" is a real, meaningful state (on `fee_share_pct` it is
 * what keeps the legacy commission in force), never a zero.
 *
 * Rounded to three decimal places of a percent — enough for 12.5% and for
 * 0.125%, and short of the float artefacts `0.07 * 100` produces
 * (7.000000000000001, which is what an ops user would otherwise see in the
 * box and dutifully save back).
 */
export function fractionToPercentInput(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(Math.round(value * 100 * 1000) / 1000);
}

/**
 * What ops typed, as the fraction the column stores.
 *
 * Returns `null` for an empty or unparseable box — an admin clearing the field
 * means "no share rule", and the API's `.nullable()` on each of these exists
 * precisely so that clearing works rather than 400ing the whole Save.
 */
export function percentInputToFraction(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  // Round the FRACTION, not the percent: 12.345% is 0.12345, and rounding at
  // five decimal places keeps every percentage an ops user can type while
  // dropping the 0.30000000000000004 a plain division leaves behind.
  return Math.round((n / 100) * 100000) / 100000;
}

export type ShareRule = 'fee_share' | 'legacy_commission';

/**
 * Which rule pays a Model 1 partner, given the draft's `fee_share_pct`.
 *
 * The same precedence the API applies (`commercial-model.ts`: a non-null
 * `fee_share_pct` wins, otherwise the pre-0241 `commission_flat +
 * commission_pct x PRICE` rule stands). It is repeated here for ONE purpose —
 * the note under the radio, which exists because the two rules read alike and
 * pay very differently: 10% of a 120 price is 12, which is 60% of a 20 fee.
 *
 * Only meaningful on Model 1. Models 2 and 3 pay from the commission and the
 * markup, and never consult either rule.
 */
export function shareRuleFor(feeSharePct: number | null | undefined): ShareRule {
  return feeSharePct == null ? 'legacy_commission' : 'fee_share';
}

/** The four rate columns, for `withOffModelRatesCleared`. */
const ALL_RATE_FIELDS: readonly CommercialRateField[] = [
  'fee_share_pct',
  'markup_pct',
  'markup_share_pct',
  'supplier_commission_share_pct',
];

/**
 * The draft with every rate OUTSIDE its model set to null, ready to save.
 *
 * The fieldset renders only the boxes its model uses, but the editor keeps
 * the whole draft, so a partner switched from Model 3 to Model 1 would
 * otherwise save with its markup share still on the row — a number ops would
 * read back later and reconcile against, and one the API's share-rule
 * derivation once mistook for a fee share. The model decides what is saved,
 * exactly as it decides what is asked; a missing model is the booking fee,
 * as it is everywhere else. The API nulls the same columns server-side when
 * the model moves; this makes the payload say so itself.
 */
export function withOffModelRatesCleared<
  T extends { commercial_model?: CommercialModel } & Partial<Record<CommercialRateField, number | null>>,
>(draft: T): T {
  const kept = new Set(fieldsForModel(draft.commercial_model ?? 'booking_fee'));
  const out = { ...draft };
  for (const field of ALL_RATE_FIELDS) if (!kept.has(field)) out[field] = null;
  return out;
}

/** True for the two models that earn from what the crew collects — the ones
 *  whose partners are served no crew without an agreed rate, and no supplier
 *  gap-fill at all. Mirrors `SUPPLIER_COMMISSION_MODELS` in
 *  apps/api/src/modules/partners/vendors.ts. */
export function earnsFromCrewTakings(model: CommercialModel): boolean {
  return model === 'supplier_commission' || model === 'markup_supplier_commission';
}
